import { makeAutoObservable } from 'mobx'
import { createContext, useContext } from 'react'
import { hash } from '../../features/specificConditions/utils'
import CatalogServiceProvider from '../../services/CatalogServiceProvider'
import { StoreInstances } from '../StoreInstancesContainer'
import { PartType } from '../models/PartsCatalogStoreModels'
import {
  CommonSpecificCondition,
  SpecificConditionAnswer,
  SpecificConditionI,
} from '../models/SpecificConditions'
import { Vehicle } from '../models/Vehicles'
import { PartsCatalogType } from '../partsCatalog/PartsCatalog'
import { cartSlice } from 'cart'
import { store } from 'store'
import ShowNotification from 'src/ui-components/modals/ToastNotifcationNotiStack'
import { ToastNotificationVariant } from 'src/ui-components/util/Interfaces'

const FITMENT_CONDITIONS_KEY = 'fitment-conditions'

export class SpecificConditionsStore {
  specificConditionsLoading = false

  specificConditionQuestions: Array<SpecificConditionI> = []

  siSpecificConditionQuestions: Array<SpecificConditionI> = []

  specificConditionAnswers: Array<SpecificConditionAnswer> = []

  terminologies: Array<PartType> = []

  constructor() {
    makeAutoObservable(this)
    this.restoreAnswersFromSession()
  }

  public findAnswer = (
    answer: CommonSpecificCondition
  ): SpecificConditionAnswer => {
    return this.specificConditionAnswers.find((a) => a.hash === answer.hash)
  }

  public findQuestion = (
    question: CommonSpecificCondition
  ): SpecificConditionI => {
    return this.specificConditionQuestions.find((q) => q.hash === question.hash)
  }

  /*
    Stores the answer in both arrays:
    * the history array
    * the vehicle array
  */
  public answerSpecificCondition = (answer: SpecificConditionAnswer): void => {
    const condition = this.findAnswer(answer)
    if (condition) condition.choice = answer.choice
    else this.specificConditionAnswers.push(answer)
    StoreInstances.searchStore.currentVehicle.specificConditions.push(answer)
  }

  public processQuestions = (): void => {
    if (!this.areAllQuestionsAnswered()) return
    StoreInstances.searchStore.currentVehicle.specificConditions = []
    this.specificConditionQuestions.forEach((q) => {
      this.answerSpecificCondition({
        ...q,
        choice: q.choice,
      })
    })
    this.updateSubModelFromSpecificConditions()
    StoreInstances.searchStore.persistCurrentVehicle()
    this.persistAnswers()
  }

  private readonly updateSubModelFromSpecificConditions = (): void => {
    const subModelQuestion = this.specificConditionQuestions.find((q) =>
      q.id.startsWith('SubModel_')
    )
    if (!subModelQuestion?.choice) return

    const subModel = {
      id: Number(subModelQuestion.choice.id),
      value: subModelQuestion.choice.value,
    }

    const { currentVehicle } = StoreInstances.searchStore
    currentVehicle.subModel = subModel

    store.dispatch(
      cartSlice.actions.updateVehicleSubModel({
        vehicle: currentVehicle,
        subModel,
      })
    )
  }

  public updateSpecificConditions = (
    specificConditions: Array<SpecificConditionAnswer>
  ): void => {
    this.specificConditionAnswers = specificConditions
    StoreInstances.searchStore.currentVehicle.specificConditions =
      specificConditions
    StoreInstances.searchStore.persistCurrentVehicle()
    this.persistAnswers()
  }

  public collectAnswers = (
    questions: Array<SpecificConditionI>
  ): Array<SpecificConditionI> => {
    return questions.map((q) => {
      const answer = this.findAnswer(q)
      if (answer) return { ...q, choice: answer.choice }
      return q
    })
  }

  public areAllQuestionsAnswered = (): boolean => {
    return !this.specificConditionQuestions.find(
      (q) => q.choice?.id === undefined
    )
  }

  public setSpecificConditionsLoading = (value: boolean): void => {
    this.specificConditionsLoading = value
  }

  public fetchSiSpecificConditionQuestions = async (
    vehicle: Vehicle,
    terminologies: Array<PartType>,
    serviceIntervalParams: {
      intervalType: number
      severeCode: string
      mileage: number
    }
  ): Promise<Array<SpecificConditionI>> => {
    this.terminologies = terminologies
    try {
      const questions = await CatalogServiceProvider.getSpecificConditions(
        vehicle,
        terminologies,
        PartsCatalogType.SERVICE_INTERVAL_SEARCH,
        serviceIntervalParams
      )
      const hashQuestions = (questions || []).map((q) => hash(q))
      this.siSpecificConditionQuestions = this.collectAnswers(hashQuestions)
      this.specificConditionQuestions = this.siSpecificConditionQuestions
      return this.siSpecificConditionQuestions
    } catch (error) {
      ShowNotification({
        message: 'Unable to retrieve specific conditions questions',
        variant: ToastNotificationVariant.ERROR,
      })
      throw new Error(error.toString())
    }
  }

  public fetchSpecificConditionQuestions = async (
    vehicle: Vehicle,
    terminologies: Array<PartType>,
    catalogType: PartsCatalogType,
    errorNotification = true,
    serviceIntervalParams?: {
      intervalType: number
      severeCode: string
      mileage: number
    }
  ): Promise<Array<SpecificConditionI>> => {
    this.terminologies = terminologies
    try {
      const questions = await CatalogServiceProvider.getSpecificConditions(
        vehicle,
        terminologies,
        catalogType,
        serviceIntervalParams
      )
      const hashQuestions = (questions || []).map((q) => hash(q))
      this.specificConditionQuestions = this.collectAnswers(hashQuestions)
      return this.specificConditionQuestions
    } catch (error) {
      if (errorNotification) {
        ShowNotification({
          message: 'Unable to retrieve specific conditions questions',
          variant: ToastNotificationVariant.ERROR,
        })
      }
      throw new Error(error.toString())
    }
  }

  public setSpecificConditionQuestions = (
    questions: Array<SpecificConditionI>
  ): void => {
    this.specificConditionQuestions = questions
  }

  public clearAnswers = (): void => {
    this.specificConditionAnswers = []
    this.persistAnswers()
  }

  private persistAnswers = (): void => {
    sessionStorage.setItem(
      FITMENT_CONDITIONS_KEY,
      JSON.stringify(this.specificConditionAnswers)
    )
  }

  private restoreAnswersFromSession = () => {
    const savedAnswersJson =
      sessionStorage.getItem(FITMENT_CONDITIONS_KEY) ?? '[]'
    this.specificConditionAnswers = JSON.parse(savedAnswersJson)
  }
}

export const SpecificConditionsContext =
  createContext<SpecificConditionsStore>(undefined)

export const useSpecificConditionStore = (): SpecificConditionsStore => {
  return useContext(SpecificConditionsContext)
}
